import { ReactNode, useState } from 'react';
import FaIcon from './FaIcon';

type AlertVariant = 'info' | 'warning' | 'danger' | 'success' | 'neutral';

interface AlertAction {
  label: string;
  onClick: () => void;
}

interface AlertProps {
  variant?: AlertVariant;
  title?: string;
  children: ReactNode;
  /** Custom icon — a string is treated as a FaIcon name (e.g. "circle-xmark"), anything else renders as-is */
  icon?: ReactNode;
  hideIcon?: boolean;
  dismissible?: boolean;
  onDismiss?: () => void;
  /** Action button — pass ReactNode for custom content or { label, onClick } for a simple link button */
  action?: ReactNode | AlertAction;
  className?: string;
  compact?: boolean;
}

const variantStyles: Record<AlertVariant, string> = {
  info: 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800 text-primary-800 dark:text-primary-200',
  warning:
    'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200',
  danger:
    'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200',
  success:
    'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200',
  // Informational, non-status-bearing content (color budget: neutral chrome)
  neutral: 'bg-surface-secondary border-theme-primary text-theme-primary',
};

const variantIcons: Record<AlertVariant, string> = {
  info: 'circle-info',
  warning: 'triangle-exclamation',
  danger: 'circle-exclamation',
  success: 'circle-check',
  neutral: 'circle-info',
};

const iconColorStyles: Record<AlertVariant, string> = {
  info: 'text-primary-500 dark:text-primary-400',
  warning: 'text-yellow-600 dark:text-yellow-400',
  danger: 'text-red-500 dark:text-red-400',
  success: 'text-green-600 dark:text-green-400',
  neutral: 'text-theme-tertiary',
};

export default function Alert({
  variant = 'info',
  title,
  children,
  icon,
  hideIcon = false,
  dismissible = false,
  onDismiss,
  action,
  className = '',
  compact = false,
}: AlertProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  const padding = compact ? 'p-2' : 'p-3';

  return (
    <div
      className={`border rounded-lg ${padding} ${variantStyles[variant]} ${className}`}
      role="alert"
    >
      <div className="flex items-start gap-2">
        {!hideIcon && (
          <span className={`flex-shrink-0 mt-0.5 ${iconColorStyles[variant]}`}>
            {typeof icon === 'string' ? (
              <FaIcon icon={icon} className="h-4 w-4" />
            ) : (
              icon || <FaIcon icon={variantIcons[variant]} className="h-4 w-4" />
            )}
          </span>
        )}
        <div className="flex-1 min-w-0">
          {title && (
            <p className="font-medium text-sm">{title}</p>
          )}
          <div className={`text-sm ${title ? 'mt-0.5' : ''}`}>{children}</div>
          {action && (
            <div className="mt-2">
              {typeof action === 'object' && action !== null && 'label' in action ? (
                <button
                  type="button"
                  onClick={(action as AlertAction).onClick}
                  className="text-sm font-medium underline hover:no-underline"
                >
                  {(action as AlertAction).label}
                </button>
              ) : (
                action
              )}
            </div>
          )}
        </div>
        {dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            className="flex-shrink-0 p-2 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Dismiss"
          >
            <FaIcon icon="xmark" className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
